import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Scene } from "@babylonjs/core/scene";
import type { Aabb } from "@townsy/contracts";
import { isSportsState, type SportsBallWire, type SportsState, type TennisCourtWire } from "@townsy/contracts";
import { SPORTS_BASKETBALL, SPORTS_BENCHES, SPORTS_HALL, SPORTS_KIOSK, SPORTS_SCOREBOARDS, SPORTS_SOCCER, SPORTS_TENNIS, sportsHoopPost } from "@townsy/world";
import { PartBatch, box, cylinder, sphere, torus } from "../../scene/core/meshKit";
import { RACKET_IDLE_ROLL, RACKET_IDLE_TILT, buildRacket, buildSportsAids } from "./aids";
import type { VenueSceneContext, VenueSceneHandle } from "../types";
import { useVenueStore } from "../venueStore";
import {
  BASKETBALL_SURFACE,
  SOCCER_SURFACE,
  paintAiBadge,
  paintBasketball,
  paintDirectory,
  paintHallFloor,
  paintNet,
  paintScoreboard,
  paintSoccer,
  paintTennis,
  surfaceMap,
  tennisSurface,
  type Ctx,
  type ScoreboardText,
} from "./paint";

/**
 * Townsy Sports Center in 3D (ADR-0016): the hall floor, the basketball court with two hoops, the soccer pitch with its
 * goal, two tennis courts with nets, benches, the lobby kiosk and the scoreboards, the three balls, the AI goalie and
 * the AI tennis players. Everything is original and built from numbers; it only DISPLAYS the server's state: ball
 * positions are dead-reckoned (velocity + gravity) between the ~10 Hz updates and eased, the held basketball is drawn
 * dribbling at its holder's rendered avatar.
 */
const G = 9.81;
const BALL_DRAW = { basketball: 0.24, soccer: 0.22, tennis: 0.17 } as const;
const COLORS = {
  post: "#3B4352",
  padding: "#2851D9",
  white: "#F4F6FA",
  rim: "#E8622A",
  bench: "#B98552",
  benchLeg: "#4A5263",
  kiosk: "#1B1F2A",
  kioskTop: "#2851D9",
  goalPost: "#F7F8FA",
  netPost: "#2F5D46",
  skin: ["#C68E5E", "#8D5A3B"],
  goalieKit: "#F2B233",
  goalieShorts: "#1B1F2A",
  tennisKit: "#F7F8FA",
  tennisShorts: "#24467E",
  cap: "#D9412B",
  shoe: "#20242E",
  racket: "#2B2F3A",
} as const;

interface Snap {
  p: Vector3;
  v: Vector3;
  at: number;
}

interface BallView {
  mesh: Mesh;
  shadow: Mesh;
  radius: number;
  snap: Snap | null;
  drawn: Vector3;
  visible: boolean;
}

interface Figure {
  root: TransformNode;
  body: Mesh;
  badge: Mesh;
  target: { x: number; z: number; facing: number; lean: number } | null;
  /** Tennis AI: the right arm (shoulder pivot) holding the racket, and its meshes. */
  arm: TransformNode | null;
  armMeshes: Mesh[];
}

function material(scene: Scene, name: string, color: string, opts: { emissive?: number; specular?: number } = {}): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = Color3.FromHexString(color);
  m.specularColor = new Color3(opts.specular ?? 0.05, opts.specular ?? 0.05, opts.specular ?? 0.05);
  if (opts.emissive) m.emissiveColor = Color3.FromHexString(color).scale(opts.emissive);
  m.ambientColor = Color3.White();
  return m;
}

function textureCtx(t: DynamicTexture): Ctx {
  return t.getContext() as unknown as Ctx;
}

function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

export function buildSportsScene(ctx: VenueSceneContext): VenueSceneHandle {
  const { scene } = ctx;
  const root = new TransformNode("sports", scene);
  const meshes: Mesh[] = [];
  const materials: StandardMaterial[] = [];
  const textures: DynamicTexture[] = [];
  const clones: Texture[] = [];
  const own = <T extends Mesh>(m: T): T => {
    m.parent = root;
    m.isPickable = false;
    meshes.push(m);
    return m;
  };
  const ownMat = (m: StandardMaterial): StandardMaterial => {
    materials.push(m);
    return m;
  };
  const dyn = (name: string, w: number, h: number, alpha = false, mips = true): DynamicTexture => {
    const t = new DynamicTexture(name, { width: w, height: h }, scene, mips, Texture.TRILINEAR_SAMPLINGMODE);
    t.hasAlpha = alpha;
    t.anisotropicFilteringLevel = 8;
    textures.push(t);
    return t;
  };

  // ---- vertex-coloured static parts (one mesh) ----
  const vc = ownMat(new StandardMaterial("sportsVc", scene));
  vc.diffuseColor = Color3.White();
  vc.specularColor = new Color3(0.06, 0.06, 0.06);
  vc.ambientColor = Color3.White();

  // ---- floors: the hall, then each court on top (lifted a few millimetres, polygon offset against the room floor) ----
  const surface = (name: string, rect: Aabb, y: number, mat: StandardMaterial, zOffset: number): Mesh => {
    const g = own(CreateGround(name, { width: rect.maxX - rect.minX, height: rect.maxZ - rect.minZ }, scene));
    g.position.set((rect.minX + rect.maxX) / 2, y, (rect.minZ + rect.maxZ) / 2);
    g.material = mat;
    mat.zOffset = zOffset;
    g.receiveShadows = true;
    g.metadata = { walkable: true };
    g.freezeWorldMatrix();
    return g;
  };
  const hallTex = dyn("sportsHallTex", 256, 256);
  paintHallFloor(textureCtx(hallTex), 256);
  hallTex.update();
  hallTex.wrapU = Texture.WRAP_ADDRESSMODE;
  hallTex.wrapV = Texture.WRAP_ADDRESSMODE;
  hallTex.uScale = (SPORTS_HALL.maxX - SPORTS_HALL.minX) / 5;
  hallTex.vScale = (SPORTS_HALL.maxZ - SPORTS_HALL.minZ) / 5;
  const hallMat = ownMat(new StandardMaterial("sportsHallMat", scene));
  hallMat.diffuseTexture = hallTex;
  hallMat.specularColor = new Color3(0.04, 0.04, 0.04);
  surface("sportsHallFloor", SPORTS_HALL, 0.004, hallMat, -1);

  const paintedSurface = (name: string, rect: Aabb, ppm: number, paint: (c: Ctx, m: ReturnType<typeof surfaceMap>) => void): StandardMaterial => {
    const map = surfaceMap(rect, ppm);
    const t = dyn(`${name}Tex`, map.width, map.height);
    paint(textureCtx(t), map);
    t.update();
    t.wrapU = Texture.CLAMP_ADDRESSMODE;
    t.wrapV = Texture.CLAMP_ADDRESSMODE;
    const m = ownMat(new StandardMaterial(`${name}Mat`, scene));
    m.diffuseTexture = t;
    m.specularColor = new Color3(0.08, 0.08, 0.08);
    return m;
  };
  surface("sportsBasketballFloor", BASKETBALL_SURFACE, 0.009, paintedSurface("sportsBasketball", BASKETBALL_SURFACE, 48, paintBasketball), -2);
  surface("sportsSoccerFloor", SOCCER_SURFACE, 0.009, paintedSurface("sportsSoccer", SOCCER_SURFACE, 40, paintSoccer), -2);
  const tennis0 = SPORTS_TENNIS.courts[0]!;
  const tennisMat = paintedSurface("sportsTennis", tennisSurface(tennis0), 40, (c, m) => paintTennis(c, m, tennis0));
  // Both courts look alike except the number: paint the second one separately.
  const tennis1 = SPORTS_TENNIS.courts[1]!;
  const tennisMat1 = paintedSurface("sportsTennis1", tennisSurface(tennis1), 40, (c, m) => paintTennis(c, m, tennis1));
  surface("sportsTennisFloor0", tennisSurface(tennis0), 0.009, tennisMat, -2);
  surface("sportsTennisFloor1", tennisSurface(tennis1), 0.009, tennisMat1, -2);

  // ---- nets: one transparent cord pattern, tinted per use ----
  const netTex = dyn("sportsNetTex", 128, 128, true);
  paintNet(textureCtx(netTex), 128, 8);
  netTex.update();
  netTex.wrapU = Texture.WRAP_ADDRESSMODE;
  netTex.wrapV = Texture.WRAP_ADDRESSMODE;
  // Round 2: rackets, swings, the aim and timing aids (aids.ts).
  const aids = buildSportsAids(scene, root, ctx.localActorId, netTex);
  const racketStrings = aids.racketMaterials.strings;
  const racketFrameMat = aids.racketMaterials.frame;
  const netMaterial = (name: string, color: string, uScale: number, vScale: number): StandardMaterial => {
    const t = netTex.clone();
    t.uScale = uScale;
    t.vScale = vScale;
    const m = ownMat(new StandardMaterial(name, scene));
    m.diffuseTexture = t;
    m.opacityTexture = t;
    m.diffuseColor = Color3.FromHexString(color);
    m.emissiveColor = Color3.FromHexString(color).scale(0.5);
    m.specularColor = Color3.Black();
    m.backFaceCulling = false;
    clones.push(t);
    return m;
  };

  const batch = new PartBatch();

  // ---- basketball: stanchions, arms, backboards, rims and nets ----
  const B = SPORTS_BASKETBALL;
  for (const h of B.hoops) {
    const post = sportsHoopPost(h);
    const dir = h.facesNorth ? 1 : -1;
    const boardZ = h.baselineZ + dir * B.backboard.fromBaseline;
    // Padded base and the post.
    batch.boxBase(0.6, 0.9, 0.6, COLORS.padding, post.x, 0, post.z);
    batch.boxBase(0.22, 3.6, 0.22, COLORS.post, post.x, 0.9, post.z);
    // Arm from the post to the board.
    const armLen = Math.abs(boardZ - post.z);
    batch.box(0.16, 0.16, armLen, COLORS.post, post.x, 3.35, (post.z + boardZ) / 2);
    batch.box(0.1, 0.5, 0.1, COLORS.post, post.x, 3.0, boardZ - dir * 0.15);
    // Backboard: white with a red-orange target square and border.
    const bw = B.backboard.width;
    const bh = B.backboard.height;
    const by = B.backboard.bottom + bh / 2;
    batch.box(bw, bh, 0.05, COLORS.white, h.x, by, boardZ);
    const face = boardZ + dir * 0.03;
    for (const [w, hh, x, y] of [
      [bw, 0.05, h.x, B.backboard.bottom + bh - 0.025],
      [bw, 0.05, h.x, B.backboard.bottom + 0.025],
      [0.05, bh, h.x - bw / 2 + 0.025, by],
      [0.05, bh, h.x + bw / 2 - 0.025, by],
      [0.59, 0.05, h.x, B.backboard.bottom + 0.15 + 0.45 - 0.025],
      [0.59, 0.05, h.x, B.backboard.bottom + 0.15 + 0.025],
      [0.05, 0.45, h.x - 0.295 + 0.025, B.backboard.bottom + 0.15 + 0.225],
      [0.05, 0.45, h.x + 0.295 - 0.025, B.backboard.bottom + 0.15 + 0.225],
    ] as const) {
      batch.box(w, hh, 0.012, COLORS.rim, x, y, face);
    }
    // Rim and its bracket.
    batch.add(torus(scene, "sportsRim", B.rimRadius * 2, 0.025, 24, COLORS.rim, { pos: { x: h.x, y: B.rimHeight, z: h.z } }));
    batch.box(0.12, 0.04, Math.abs(h.z - boardZ) - B.rimRadius + 0.02, COLORS.rim, h.x, B.rimHeight, (boardZ + h.z - dir * B.rimRadius) / 2);
    // Net (open cone of cords).
    const net = own(CreateCylinder("sportsHoopNet", { height: 0.42, diameterTop: B.rimRadius * 2, diameterBottom: B.rimRadius * 1.3, tessellation: 16, cap: Mesh.NO_CAP }, scene));
    net.position.set(h.x, B.rimHeight - 0.21, h.z);
    net.material = netMaterial(`sportsHoopNetMat${h.id}`, "#FFFFFF", 2, 0.5);
  }

  // ---- soccer goal: posts, crossbar, frame and nets ----
  const S = SPORTS_SOCCER;
  const gl = S.goal;
  const gx0 = gl.x - gl.halfWidth - gl.postRadius;
  const gx1 = gl.x + gl.halfWidth + gl.postRadius;
  const back = gl.lineZ + gl.depth;
  const pd = gl.postRadius * 2 + 0.04;
  for (const x of [gx0, gx1]) {
    batch.add(cylinder(scene, "sportsGoalPost", { height: gl.height, diameter: pd, tessellation: 12 }, COLORS.goalPost, { pos: { x, y: gl.height / 2, z: gl.lineZ } }));
    // Back frame: a slim post at the back and the top bar going back.
    batch.add(cylinder(scene, "sportsGoalBackPost", { height: gl.height * 0.75, diameter: 0.06, tessellation: 8 }, COLORS.goalPost, { pos: { x, y: (gl.height * 0.75) / 2, z: back } }));
    batch.box(0.05, 0.05, gl.depth, COLORS.goalPost, x, gl.height - 0.03, gl.lineZ + gl.depth / 2);
    batch.box(0.05, 0.05, gl.depth, COLORS.goalPost, x, 0.03, gl.lineZ + gl.depth / 2);
  }
  batch.add(cylinder(scene, "sportsGoalBar", { height: gx1 - gx0 + pd, diameter: pd, tessellation: 12 }, COLORS.goalPost, { pos: { x: gl.x, y: gl.height, z: gl.lineZ }, rot: { z: Math.PI / 2 } }));
  batch.box(gx1 - gx0, 0.05, 0.05, COLORS.goalPost, gl.x, 0.03, back);
  batch.box(gx1 - gx0, 0.05, 0.05, COLORS.goalPost, gl.x, gl.height * 0.75, back);
  const goalNet = netMaterial("sportsGoalNetMat", "#FFFFFF", (gx1 - gx0) / 0.9, gl.height / 0.9);
  const goalSideNet = netMaterial("sportsGoalSideNetMat", "#FFFFFF", gl.depth / 0.9, gl.height / 0.9);
  const goalTopNet = netMaterial("sportsGoalTopNetMat", "#FFFFFF", (gx1 - gx0) / 0.9, gl.depth / 0.9);
  const netPlane = (name: string, w: number, h: number, mat: StandardMaterial, pos: Vector3, rot: Vector3): void => {
    const p = own(CreatePlane(name, { width: w, height: h, sideOrientation: Mesh.DOUBLESIDE }, scene));
    p.position.copyFrom(pos);
    p.rotation.copyFrom(rot);
    p.material = mat;
  };
  // Back net slopes from the crossbar down to the back bar.
  const slopeLen = Math.hypot(gl.depth, gl.height * 0.25);
  netPlane("sportsGoalNetBack", gx1 - gx0, gl.height * 0.75, goalNet, new Vector3(gl.x, (gl.height * 0.75) / 2, back), new Vector3(0, 0, 0));
  netPlane("sportsGoalNetTop", gx1 - gx0, slopeLen, goalTopNet, new Vector3(gl.x, gl.height * 0.875, gl.lineZ + gl.depth / 2), new Vector3(Math.PI / 2 + Math.atan2(gl.height * 0.25, gl.depth), 0, 0));
  for (const x of [gx0, gx1]) netPlane("sportsGoalNetSide", gl.depth, gl.height * 0.9, goalSideNet, new Vector3(x, (gl.height * 0.9) / 2, gl.lineZ + gl.depth / 2), new Vector3(0, Math.PI / 2, 0));

  // ---- tennis nets ----
  const T = SPORTS_TENNIS;
  for (const c of T.courts) {
    const [p0, p1] = c.netPosts;
    for (const p of [p0, p1]) batch.add(cylinder(scene, "sportsNetPost", { height: 1.07, diameter: 0.12, tessellation: 10 }, COLORS.netPost, { pos: { x: p.x, y: 0.535, z: p.z } }));
    const w = p1.x - p0.x;
    netPlane("sportsTennisNet", w, T.netHeight - 0.06, netMaterial(`sportsTennisNetMat${c.id}`, "#20262F", w / 0.6, (T.netHeight - 0.06) / 0.6), new Vector3(c.centreX, (T.netHeight - 0.06) / 2, c.netZ), new Vector3(0, 0, 0));
    batch.box(w, 0.06, 0.04, COLORS.white, c.centreX, T.netHeight - 0.03, c.netZ);
    batch.box(0.05, T.netHeight, 0.03, COLORS.white, c.centreX, T.netHeight / 2, c.netZ);
  }

  // ---- benches, kiosk and scoreboard stands ----
  for (const b of SPORTS_BENCHES) {
    const w = b.box.maxX - b.box.minX;
    const d = b.box.maxZ - b.box.minZ;
    const cx = (b.box.minX + b.box.maxX) / 2;
    const cz = (b.box.minZ + b.box.maxZ) / 2;
    const alongX = w >= d;
    const len = alongX ? w : d;
    batch.box(alongX ? w : w * 0.9, 0.07, alongX ? d * 0.9 : d, COLORS.bench, cx, 0.45, cz);
    const legs = Math.max(2, Math.round(len / 2.2));
    for (let i = 0; i < legs; i++) {
      const t = legs === 1 ? 0.5 : i / (legs - 1);
      const along = -len / 2 + 0.15 + t * (len - 0.3);
      batch.boxBase(alongX ? 0.06 : w * 0.8, 0.42, alongX ? d * 0.8 : 0.06, COLORS.benchLeg, alongX ? cx + along : cx, 0, alongX ? cz : cz + along);
    }
    // A backrest on the wall benches (against the wall side).
    const nearWall = b.id === "venue_bench_sw" || b.id === "venue_bench_se" || b.id === "venue_bench_w" || b.id === "venue_bench_e";
    if (nearWall) {
      if (alongX) batch.boxBase(w, 0.42, 0.06, COLORS.bench, cx, 0.52, b.box.minZ + 0.04);
      else batch.boxBase(0.06, 0.42, d, COLORS.bench, b.id === "venue_bench_w" ? b.box.minX + 0.04 : b.box.maxX - 0.04, 0.52, cz);
    }
  }
  const K = SPORTS_KIOSK;
  const kx = (K.minX + K.maxX) / 2;
  const kz = (K.minZ + K.maxZ) / 2;
  batch.boxBase(K.maxX - K.minX, 0.3, K.maxZ - K.minZ, COLORS.kiosk, kx, 0, kz);
  batch.boxBase(K.maxX - K.minX + 0.06, 0.06, K.maxZ - K.minZ + 0.06, COLORS.kioskTop, kx, 0.3, kz);
  for (const sx of [-1, 1]) batch.boxBase(0.14, 3.7, 0.14, COLORS.post, kx + sx * 2.35, 0.36, kz);
  batch.box(4.86, 0.12, 0.18, COLORS.post, kx, 4.06, kz);

  const boardStand = (x: number, w: number, z: number, y0: number, h: number) => {
    for (const sx of [-1, 1]) batch.boxBase(0.16, y0 + h, 0.16, COLORS.post, x + sx * (w / 2 - 0.3), 0, z + 0.12);
    batch.box(w + 0.2, h + 0.2, 0.12, COLORS.post, x, y0 + h / 2, z + 0.1);
  };
  const BOARD_Y = 4.0;
  const BOARD_H = 2.8;
  const BOARD_Z = SPORTS_HALL.maxZ - 0.5;
  boardStand(SPORTS_SCOREBOARDS.soccer.x, SPORTS_SCOREBOARDS.soccer.width, BOARD_Z, BOARD_Y, BOARD_H);
  boardStand(SPORTS_SCOREBOARDS.basketball.x, SPORTS_SCOREBOARDS.basketball.width, BOARD_Z, BOARD_Y, BOARD_H);
  for (const t of SPORTS_SCOREBOARDS.tennis) boardStand(t.x, t.width, BOARD_Z, BOARD_Y, BOARD_H);

  const statics = batch.build("sportsStatic", scene);
  if (statics) {
    own(statics);
    statics.material = vc;
    statics.receiveShadows = true;
    statics.freezeWorldMatrix();
  }

  // ---- boards with live text (unlit) ----
  interface Board {
    tex: DynamicTexture;
    key: string;
    w: number;
    h: number;
  }
  const boards = new Map<string, Board>();
  const makeBoard = (name: string, x: number, y: number, z: number, w: number, h: number, facing: number, pxW: number): Board => {
    const pxH = Math.round((pxW * h) / w);
    const tex = dyn(`${name}Tex`, pxW, pxH, false, true);
    const mat = ownMat(new StandardMaterial(`${name}Mat`, scene));
    mat.disableLighting = true;
    mat.emissiveTexture = tex;
    mat.diffuseColor = Color3.Black();
    mat.emissiveColor = Color3.Black();
    mat.ambientColor = Color3.Black();
    mat.specularColor = Color3.Black();
    const plane = own(CreatePlane(name, { width: w, height: h }, scene));
    plane.position.set(x, y, z);
    // A plane faces -z by default; turn it to face `facing` (yaw).
    plane.rotation.y = facing + Math.PI;
    plane.material = mat;
    const b: Board = { tex, key: "", w: pxW, h: pxH };
    boards.set(name, b);
    return b;
  };
  const SOUTH = Math.PI;
  makeBoard("sportsBoardSoccer", SPORTS_SCOREBOARDS.soccer.x, BOARD_Y + BOARD_H / 2, BOARD_Z - 0.01, SPORTS_SCOREBOARDS.soccer.width, BOARD_H, SOUTH, 1024);
  makeBoard("sportsBoardBasketball", SPORTS_SCOREBOARDS.basketball.x, BOARD_Y + BOARD_H / 2, BOARD_Z - 0.01, SPORTS_SCOREBOARDS.basketball.width, BOARD_H, SOUTH, 1024);
  SPORTS_SCOREBOARDS.tennis.forEach((t, i) => makeBoard(`sportsBoardTennis${i}`, t.x, BOARD_Y + BOARD_H / 2, BOARD_Z - 0.01, t.width, BOARD_H, SOUTH, 1024));
  makeBoard("sportsKioskSouth", kx, 2.2, kz - 0.06, 4.6, 3.0, SOUTH, 1024);
  makeBoard("sportsKioskNorth", kx, 2.2, kz + 0.06, 4.6, 3.0, 0, 1024);

  const paintBoard = (name: string, t: ScoreboardText): void => {
    const b = boards.get(name);
    if (!b) return;
    const key = JSON.stringify(t);
    if (key === b.key) return;
    b.key = key;
    paintScoreboard(textureCtx(b.tex), b.w, b.h, t);
    b.tex.update();
  };
  const paintKiosk = (live: string[]): void => {
    for (const name of ["sportsKioskSouth", "sportsKioskNorth"]) {
      const b = boards.get(name);
      if (!b) continue;
      const key = live.join("|");
      if (key === b.key) continue;
      b.key = key;
      paintDirectory(textureCtx(b.tex), b.w, b.h, live);
      b.tex.update();
    }
  };

  // ---- balls with soft blob shadows ----
  const blobTex = dyn("sportsBlobTex", 64, 64, true, false);
  {
    const c = textureCtx(blobTex);
    const g = c.createRadialGradient(32, 32, 2, 32, 32, 31);
    g.addColorStop(0, "rgba(0,0,0,0.55)");
    g.addColorStop(1, "rgba(0,0,0,0)");
    c.fillStyle = g;
    c.fillRect(0, 0, 64, 64);
    blobTex.update();
  }
  const blobMat = ownMat(new StandardMaterial("sportsBlobMat", scene));
  blobMat.diffuseTexture = blobTex;
  blobMat.opacityTexture = blobTex;
  blobMat.diffuseColor = Color3.Black();
  blobMat.specularColor = Color3.Black();
  blobMat.disableLighting = true;
  blobMat.zOffset = -4;
  const ballTexture = (name: string, paint: (c: Ctx, w: number, h: number) => void): StandardMaterial => {
    const t = dyn(`${name}Tex`, 128, 64);
    paint(textureCtx(t), 128, 64);
    t.update();
    const m = ownMat(new StandardMaterial(`${name}Mat`, scene));
    m.diffuseTexture = t;
    m.specularColor = new Color3(0.12, 0.12, 0.12);
    m.emissiveColor = new Color3(0.08, 0.08, 0.08);
    return m;
  };
  const basketMat = ballTexture("sportsBasketBall", (c, w, h) => {
    c.fillStyle = "#E06A1E";
    c.fillRect(0, 0, w, h);
    c.strokeStyle = "#2A1A10";
    c.lineWidth = 3;
    for (const x of [0, w / 4, w / 2, (w * 3) / 4]) {
      c.beginPath();
      c.moveTo(x, 0);
      c.lineTo(x, h);
      c.stroke();
    }
    c.beginPath();
    c.moveTo(0, h / 2);
    c.lineTo(w, h / 2);
    c.stroke();
  });
  const soccerMat = ballTexture("sportsSoccerBall", (c, w, h) => {
    c.fillStyle = "#F7F7F7";
    c.fillRect(0, 0, w, h);
    c.fillStyle = "#1E2028";
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 3; j++) {
        const x = (i + (j % 2) * 0.5) * (w / 6) + 4;
        const y = (j + 0.5) * (h / 3);
        c.beginPath();
        for (let k = 0; k < 5; k++) {
          const a = (k / 5) * Math.PI * 2 - Math.PI / 2;
          const r = h / 9;
          if (k === 0) c.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
          else c.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
        }
        c.closePath();
        c.fill();
      }
    }
  });
  const tennisBallMat = ownMat(material(scene, "sportsTennisBallMat", "#DDF03C", { emissive: 0.55, specular: 0.1 }));
  const makeBall = (name: string, diameter: number, mat: StandardMaterial): BallView => {
    const mesh = own(CreateSphere(name, { diameter, segments: 12 }, scene));
    mesh.material = mat;
    const shadow = own(CreateDisc(`${name}Shadow`, { radius: diameter * 0.75, tessellation: 20 }, scene));
    shadow.rotation.x = Math.PI / 2;
    shadow.material = blobMat;
    mesh.setEnabled(false);
    shadow.setEnabled(false);
    return { mesh, shadow, radius: diameter / 2, snap: null, drawn: new Vector3(0, -10, 0), visible: false };
  };
  const basketball = makeBall("sportsBasketballBall", BALL_DRAW.basketball, basketMat);
  const soccerBall = makeBall("sportsSoccerBallMesh", BALL_DRAW.soccer, soccerMat);
  const tennisBalls = T.courts.map((c) => makeBall(`sportsTennisBall${c.id}`, BALL_DRAW.tennis, tennisBallMat));

  // ---- the AI figures (goalie and one tennis player per court), labelled "AI" ----
  const badge = (name: string, subtitle: string): Mesh => {
    const t = dyn(`${name}Tex`, 256, 128, true, true);
    paintAiBadge(textureCtx(t), 256, 128, subtitle);
    t.update();
    const m = ownMat(new StandardMaterial(`${name}Mat`, scene));
    m.disableLighting = true;
    m.emissiveTexture = t;
    m.opacityTexture = t;
    m.diffuseColor = Color3.Black();
    m.specularColor = Color3.Black();
    m.backFaceCulling = false;
    const p = own(CreatePlane(name, { width: 1.0, height: 0.5 }, scene));
    p.material = m;
    p.billboardMode = Mesh.BILLBOARDMODE_ALL;
    return p;
  };
  const figure = (name: string, kit: { shirt: string; shorts: string; skin: string; goalie: boolean }, subtitle: string): Figure => {
    const fb = new PartBatch();
    for (const sx of [-1, 1]) {
      fb.boxBase(0.15, 0.08, 0.26, COLORS.shoe, sx * 0.11, 0, 0.04);
      fb.boxBase(0.14, 0.42, 0.15, kit.goalie ? kit.shirt : "#F2F2F2", sx * 0.11, 0.08, 0);
      fb.boxBase(0.15, 0.36, 0.17, kit.skin, sx * 0.11, 0.5, 0);
    }
    fb.boxBase(0.42, 0.24, 0.24, kit.shorts, 0, 0.78, 0);
    fb.boxBase(0.46, 0.58, 0.26, kit.shirt, 0, 1.0, 0);
    fb.add(sphere(scene, `${name}Head`, 0.27, 10, kit.skin, { pos: { x: 0, y: 1.74, z: 0 } }));
    fb.boxBase(0.1, 0.12, 0.1, kit.skin, 0, 1.56, 0);
    if (kit.goalie) {
      // Arms up and out, ready to save; big gloves.
      for (const sx of [-1, 1]) {
        fb.add(box(scene, `${name}Arm`, 0.12, 0.6, 0.12, kit.shirt, { pos: { x: sx * 0.42, y: 1.6, z: 0.05 }, rot: { z: -sx * 0.75 } }));
        fb.add(box(scene, `${name}Glove`, 0.17, 0.17, 0.1, "#3FBF6A", { pos: { x: sx * 0.64, y: 1.84, z: 0.08 } }));
      }
      fb.add(sphere(scene, `${name}Hair`, 0.28, 8, "#2A1E16", { pos: { x: 0, y: 1.8, z: -0.02 } }));
    } else {
      fb.add(box(scene, `${name}ArmL`, 0.11, 0.56, 0.11, kit.skin, { pos: { x: -0.3, y: 1.22, z: 0 }, rot: { z: 0.12 } }));
      // Cap. The right arm (with the racket) is its own node below: it swings.
      fb.add(sphere(scene, `${name}Cap`, 0.29, 8, COLORS.cap, { pos: { x: 0, y: 1.8, z: -0.01 } }));
      fb.box(0.2, 0.025, 0.14, COLORS.cap, 0, 1.79, 0.15);
    }
    const body = fb.build(name, scene)!;
    body.material = vc;
    const fRoot = new TransformNode(`${name}Root`, scene);
    fRoot.parent = root;
    body.parent = fRoot;
    body.isPickable = false;
    meshes.push(body);
    let arm: TransformNode | null = null;
    let armMeshes: Mesh[] = [];
    if (!kit.goalie) {
      // Right arm hanging from the shoulder, the racket in its hand (tilted forward at rest); the pivot is the shoulder.
      arm = new TransformNode(`${name}ArmR`, scene);
      arm.parent = fRoot;
      arm.position.set(0.3, 1.47, 0);
      arm.rotation.z = 0.12;
      const ab = new PartBatch();
      ab.boxBase(0.11, 0.56, 0.11, kit.skin, 0, -0.56, 0);
      ab.add(sphere(scene, `${name}Hand`, 0.1, 6, kit.skin, { pos: { x: 0, y: -0.58, z: 0 } }));
      const armMesh = ab.build(`${name}ArmMesh`, scene)!;
      armMesh.material = vc;
      armMesh.parent = arm;
      armMesh.isPickable = false;
      meshes.push(armMesh);
      const racket = buildRacket(scene, `${name}Racket`, racketStrings, racketFrameMat);
      racket.root.name = `${name}RacketRoot`;
      racket.root.parent = arm;
      racket.root.position.set(0, -0.6, 0.02);
      racket.root.rotation.set(RACKET_IDLE_TILT, 0, RACKET_IDLE_ROLL);
      for (const m of racket.meshes) meshes.push(m);
      armMeshes = [armMesh, racket.meshes[0]!];
    }
    const b = badge(`${name}Badge`, subtitle);
    b.parent = fRoot;
    b.position.set(0, 2.45, 0);
    fRoot.setEnabled(false);
    return { root: fRoot, body, badge: b, target: null, arm, armMeshes };
  };
  const goalie = figure("sportsGoalie", { shirt: COLORS.goalieKit, shorts: COLORS.goalieShorts, skin: COLORS.skin[0], goalie: true }, "goalie");
  goalie.root.position.set(gl.x, 0, S.goalieLineZ);
  goalie.root.rotation.y = Math.PI;
  goalie.root.setEnabled(true);
  const tennisAi = T.courts.map((c) => figure(`sportsTennisAi${c.id}`, { shirt: COLORS.tennisKit, shorts: COLORS.tennisShorts, skin: COLORS.skin[1], goalie: false }, "tennis"));

  aids.setAiArms(tennisAi.map((f) => f.arm));
  ctx.addShadowCasters([...(statics ? [statics] : []), basketball.mesh, soccerBall.mesh, ...tennisBalls.map((b) => b.mesh), goalie.body, ...tennisAi.flatMap((f) => [f.body, ...f.armMeshes])]);

  // ---- state ----
  let state: SportsState | null = null;
  const snapOf = (w: SportsBallWire, at: number): Snap => ({ p: new Vector3(w[0], w[1], w[2]), v: new Vector3(w[3], w[4], w[5]), at });
  const setBall = (b: BallView, w: SportsBallWire | null, at: number) => {
    if (!w) {
      b.snap = null;
      b.visible = false;
      return;
    }
    const s = snapOf(w, at);
    // Teleports (a reset to the spot or the centre) snap instead of gliding across the hall.
    if (!b.visible || Vector3.Distance(b.drawn, s.p) > 3) b.drawn.copyFrom(s.p);
    b.snap = s;
    b.visible = true;
  };

  const tennisLines = (c: TennisCourtWire): string[] => {
    if (c.phase === "open") return ["Open", "Stand on a side and press Play"];
    const [a, b] = c.sides;
    return [`${a.name} ${a.points} - ${b.points} ${b.name}`, c.message || (c.phase === "rally" ? "Rally" : ""), "First to 7, win by 2"].filter(Boolean);
  };

  const update = (raw: unknown): void => {
    if (!isSportsState(raw)) return;
    state = raw;
    aids.update(raw);
    const now = performance.now();
    setBall(basketball, raw.basketball.ball, now);
    setBall(soccerBall, raw.soccer.ball, now);
    raw.tennis.forEach((c, i) => {
      const view = tennisBalls[i];
      if (view) setBall(view, c.phase === "open" || c.phase === "waiting" ? null : c.ball, now);
      const f = tennisAi[i];
      if (f) {
        if (c.ai) {
          const first = f.target === null;
          f.target = { x: c.ai[0], z: c.ai[1], facing: c.ai[2], lean: 0 };
          if (first) {
            f.root.position.set(c.ai[0], 0, c.ai[1]);
            f.root.rotation.y = c.ai[2];
          }
          f.root.setEnabled(true);
        } else {
          f.target = null;
          f.root.setEnabled(false);
        }
      }
    });
    goalie.target = { x: raw.soccer.goalie.x, z: raw.soccer.goalie.z, facing: Math.PI, lean: raw.soccer.goalie.lean };
    // Boards.
    const bb = raw.basketball;
    paintBoard("sportsBoardBasketball", {
      title: "BASKETBALL",
      lines: bb.top.length ? bb.top.slice(0, 3).map((t, i) => `${i + 1}. ${t.name}  ${t.points}`) : ["Shoot some hoops!", "2 inside the arc, 3 beyond"],
    });
    const sc = raw.soccer;
    paintBoard("sportsBoardSoccer", {
      title: "SOCCER vs AI GOALIE",
      lines: [`GOALS ${sc.goals}   SAVES ${sc.saves}`, ...sc.top.slice(0, 2).map((t) => `${t.name}: ${t.goals} ${t.goals === 1 ? "goal" : "goals"}`)],
    });
    raw.tennis.forEach((c, i) => paintBoard(`sportsBoardTennis${i}`, { title: `TENNIS COURT ${i + 1}`, lines: tennisLines(c) }));
    const live: string[] = [];
    if (bb.top[0]) live.push(`Top shooter: ${bb.top[0].name} (${bb.top[0].points})`);
    if (sc.goals || sc.saves) live.push(`Soccer: ${sc.goals} goals, ${sc.saves} saves`);
    paintKiosk(live);
  };

  // Paint the boards before the first state arrives.
  update({
    basketball: { ball: [B.centre.x, 0.12, B.centre.z, 0, 0, 0], phase: "loose", holder: null, holderName: null, top: [] },
    soccer: { ball: [S.penaltySpot.x, 0.11, S.penaltySpot.z, 0, 0, 0], phase: "live", goalie: { x: gl.x, z: S.goalieLineZ, lean: 0, holding: false }, goals: 0, saves: 0, top: [] },
    tennis: T.courts.map(() => ({ phase: "open", ball: null, sides: [{ kind: "open", actorId: null, name: "Open", points: 0 }, { kind: "open", actorId: null, name: "Open", points: 0 }], lastHitter: null, bounces: 0, swings: [{ n: 0, hand: "fore", hit: false }, { n: 0, hand: "fore", hit: false }], server: 0, ai: null, message: "" })),
  } satisfies SportsState);
  // The room may have received the game's state before this scene was built.
  const stored = useVenueStore.getState();
  if (stored.venue === "sports_center" && stored.shared) update(stored.shared);

  // ---- per frame: dead reckoning, easing, dribble, figures ----
  let dribble = 0;
  let holderNode: TransformNode | null = null;
  let holderId: string | null = null;
  const findAvatar = (actorId: string): TransformNode | null => {
    if (holderId === actorId && holderNode && !holderNode.isDisposed()) return holderNode;
    holderId = actorId;
    holderNode = scene.getTransformNodeByName(`av_${actorId}`);
    return holderNode;
  };
  const tmp = new Vector3();
  const drawBall = (b: BallView, dtS: number, nowMs: number): void => {
    if (!b.visible || !b.snap) {
      b.mesh.setEnabled(false);
      b.shadow.setEnabled(false);
      return;
    }
    const s = b.snap;
    const tau = Math.min(0.3, Math.max(0, (nowMs - s.at) / 1000));
    tmp.set(s.p.x + s.v.x * tau, s.p.y + s.v.y * tau - (s.p.y > b.radius + 0.01 || s.v.y > 0 ? 0.5 * G * tau * tau : 0), s.p.z + s.v.z * tau);
    if (tmp.y < b.radius) tmp.y = b.radius;
    const k = 1 - Math.exp(-dtS * 22);
    b.drawn.x += (tmp.x - b.drawn.x) * k;
    b.drawn.y += (tmp.y - b.drawn.y) * k;
    b.drawn.z += (tmp.z - b.drawn.z) * k;
    placeBall(b);
  };
  const placeBall = (b: BallView): void => {
    b.mesh.setEnabled(true);
    b.shadow.setEnabled(true);
    b.mesh.position.copyFrom(b.drawn);
    b.shadow.position.set(b.drawn.x, 0.02, b.drawn.z);
    const lift = Math.max(0, b.drawn.y - b.radius);
    const s = 1 + lift * 0.35;
    b.shadow.scaling.set(s, s, s);
    b.shadow.visibility = Math.max(0.15, 1 - lift * 0.18);
  };

  const frame = (dtMs: number, nowMs: number): void => {
    const dtS = Math.min(0.1, Math.max(0, dtMs / 1000));
    const st = state;
    // Basketball: dribbling at the holder's drawn avatar when we can find it.
    const holder = st?.basketball.phase === "held" ? st.basketball.holder : null;
    const node = holder ? findAvatar(holder) : null;
    if (node) {
      dribble += dtS * Math.PI * 2 * 1.7;
      const f = node.rotation.y;
      const fx = Math.sin(f);
      const fz = Math.cos(f);
      basketball.drawn.set(node.position.x + fx * 0.42 + fz * 0.2, BALL_DRAW.basketball / 2 + Math.abs(Math.sin(dribble)) * 0.8, node.position.z + fz * 0.42 - fx * 0.2);
      basketball.visible = true;
      placeBall(basketball);
      basketball.mesh.rotation.x += dtS * 6;
    } else {
      drawBall(basketball, dtS, nowMs);
      if (basketball.snap) basketball.mesh.rotation.x += dtS * Math.hypot(basketball.snap.v.x, basketball.snap.v.z) * 4;
    }
    drawBall(soccerBall, dtS, nowMs);
    if (soccerBall.snap) {
      const sp = Math.hypot(soccerBall.snap.v.x, soccerBall.snap.v.z);
      soccerBall.mesh.rotation.x += dtS * sp * 8;
    }
    tennisBalls.forEach((b) => drawBall(b, dtS, nowMs));
    aids.frame(dtS, nowMs, { basketball: basketball.drawn, soccer: soccerBall.drawn, tennis: tennisBalls.map((b) => (b.visible ? b.drawn : null)) });
    // Goalie: glide along the line, lean into dives.
    if (goalie.target) {
      const k = 1 - Math.exp(-dtS * 14);
      goalie.root.position.x += (goalie.target.x - goalie.root.position.x) * k;
      // The goalie faces south, so its own left (+z roll) is the world's east.
      goalie.root.rotation.z += (goalie.target.lean * 0.5 - goalie.root.rotation.z) * k;
      goalie.root.position.y = Math.abs(goalie.target.lean) > 0.4 ? 0.08 : 0;
    }
    for (const f of tennisAi) {
      if (!f.target) continue;
      const k = 1 - Math.exp(-dtS * 12);
      const dx = f.target.x - f.root.position.x;
      const dz = f.target.z - f.root.position.z;
      f.root.position.x += dx * k;
      f.root.position.z += dz * k;
      f.root.rotation.y = lerpAngle(f.root.rotation.y, f.target.facing, k);
      const moving = Math.hypot(dx, dz) > 0.05;
      f.body.position.y = moving ? Math.abs(Math.sin(nowMs / 90)) * 0.05 : 0;
    }
  };

  return {
    update,
    frame,
    dispose() {
      aids.dispose();
      for (const m of meshes) if (!m.isDisposed()) m.dispose(false, false);
      for (const f of [goalie, ...tennisAi]) f.root.dispose();
      for (const m of materials) m.dispose(false, false);
      for (const t of clones) t.dispose();
      for (const t of textures) t.dispose();
      root.dispose();
      boards.clear();
      state = null;
    },
  };
}
